import Konva from 'konva';
import { Layer } from 'konva/lib/Layer';

const LINE_EXTENT = 20000; // half-length in content space — always spans visible area
const HANDLE_W    = 14;
const HANDLE_H    = 8;

/**
 * Dedicated Konva layer that renders persistent guideline lines and their
 * edge handles.  All state lives in GuidelineManager; this layer is purely visual.
 *
 * Handles sit at x=0 (for horizontal lines) or y=0 (for vertical lines) in
 * content space, which maps to the ruler/canvas boundary on screen.  They are
 * draggable so the user can reposition a guideline by dragging the handle.
 */
export default class GuidelineLayer extends Layer {
  _lineNodes  = new Map(); // id → Konva.Line
  _handleNodes = new Map(); // id → Konva.Group

  constructor({ guidelineManager, stage }) {
    super({ draggable: false, listening: true });
    this.guidelineManager = guidelineManager;
    this._stage = stage;

    guidelineManager.setOnChange((event, gl) => {
      if (event === 'add')    this._addGuide(gl);
      if (event === 'update') this._updateGuide(gl);
      if (event === 'remove') this._removeGuide(gl);
    });
  }

  redrawAll() {
    this.destroyChildren();
    this._lineNodes.clear();
    this._handleNodes.clear();
    for (const gl of this.guidelineManager.getGuidelines()) {
      this._addGuide(gl);
    }
    this.batchDraw();
  }

  // ── private helpers ─────────────────────────────────────────────────────

  _addGuide(gl) {
    const line   = this._makeLine(gl);
    const handle = this._makeHandle(gl);
    this._lineNodes.set(gl.id, line);
    this._handleNodes.set(gl.id, handle);
    this.add(line);
    this.add(handle);
    this.batchDraw();
  }

  _updateGuide(gl) {
    const line   = this._lineNodes.get(gl.id);
    const handle = this._handleNodes.get(gl.id);
    if (!line || !handle) return;
    if (gl.orientation === 'vertical') {
      line.points([ gl.position, -LINE_EXTENT, gl.position, LINE_EXTENT ]);
      handle.x(gl.position);
    } else {
      line.points([ -LINE_EXTENT, gl.position, LINE_EXTENT, gl.position ]);
      handle.y(gl.position);
    }
    this.batchDraw();
  }

  _removeGuide(gl) {
    this._lineNodes.get(gl.id)?.destroy();
    this._handleNodes.get(gl.id)?.destroy();
    this._lineNodes.delete(gl.id);
    this._handleNodes.delete(gl.id);
    this.batchDraw();
  }

  _makeLine(gl) {
    const points = gl.orientation === 'vertical'
      ? [ gl.position, -LINE_EXTENT, gl.position,  LINE_EXTENT ]
      : [ -LINE_EXTENT, gl.position,  LINE_EXTENT, gl.position ];

    return new Konva.Line({
      points,
      stroke: gl.color,
      strokeWidth: 1,
      opacity: 0.9,
      dash: [6, 4],
      listening: false,
      perfectDrawEnabled: false,
    });
  }

  _makeHandle(gl) {
    const isV = gl.orientation === 'vertical';
    const group = new Konva.Group({
      x: isV ? gl.position : 0,
      y: isV ? 0 : gl.position,
      draggable: true,
      dragBoundFunc: (absPos) => this._handleDragBound(gl, absPos),
      cursor: isV ? 'ew-resize' : 'ns-resize',
    });

    // Small rectangle tab
    const rect = new Konva.Rect({
      x: isV ? -HANDLE_W / 2 : 0,
      y: isV ? 0 : -HANDLE_H / 2,
      width:  isV ? HANDLE_W : HANDLE_W,
      height: isV ? HANDLE_W : HANDLE_H,
      fill: gl.color,
      cornerRadius: 2,
      listening: true,
    });

    // Triangular pointer toward canvas
    const arrowPoints = isV
      ? [ 0, HANDLE_W, -HANDLE_H / 2, HANDLE_W + HANDLE_H, HANDLE_H / 2, HANDLE_W + HANDLE_H ]
      : [ HANDLE_W, 0, HANDLE_W + HANDLE_H, -HANDLE_H / 2, HANDLE_W + HANDLE_H, HANDLE_H / 2 ];

    const arrow = new Konva.Line({
      points: arrowPoints,
      fill: gl.color,
      closed: true,
      listening: false,
    });

    group.add(rect, arrow);

    group.on('dragmove', () => {
      const s = this._stage;
      const abs = group.getAbsolutePosition();
      const rawPos = isV
        ? (abs.x - s.x()) / s.scaleX()
        : (abs.y - s.y()) / s.scaleY();
      const snapped = this.guidelineManager.updatePosition(gl.id, rawPos);
      // Correct handle position to snapped value
      if (isV) {
        group.x(snapped);
        group.y(0);
      } else {
        group.x(0);
        group.y(snapped);
      }
    });

    group.on('mouseenter', () => {
      document.body.style.cursor = isV ? 'ew-resize' : 'ns-resize';
    });
    group.on('mouseleave', () => {
      document.body.style.cursor = '';
    });

    return group;
  }

  _handleDragBound(gl, absPos) {
    const s = this._stage;
    if (gl.orientation === 'vertical') {
      // Lock Y to 0 in content space (handle stays at top edge)
      const handleY = s.y(); // content y=0 maps to s.y() in abs space
      return { x: absPos.x, y: handleY };
    } else {
      const handleX = s.x();
      return { x: handleX, y: absPos.y };
    }
  }
}
